import i18n from 'i18next';

import store from '../../store';

// Whole-word "wafer"/"Wafer(s)", "wafer"-prefixed compounds
// ("Wafermap", "Waferbook"), and "Klarf" → "panel"/"Panel(s)"/
// "Panelmap"/"Panelbook"/"Emap" key derivation.
// Word-boundary matching deliberately excludes glued identifiers
// like "WaferSk" or "Locxwafer_skum" (technical/column keys, not UI copy).
// The negative lookahead excludes the literal "wafermap.txt" filename —
// that's an actual backend-generated file name, not display terminology,
// so it must never be rewritten to "panelmap.txt".
const WORD_RE =
  /\b(wafer*|wafermap|Wafermap|waferbook|wafers?|WaferId|klarf)\b(?!\.txt)/gi;

const PANEL_WORD = {
  wafer: 'panel',
  wafers: 'panels',
  wafermap: 'panelmap',
  waferbook: 'panelbook',
  waferid: 'panelid',
  klarf: 'emap',
};

function toSentenceCase(match) {
  return match.charAt(0).toUpperCase() + match.slice(1);
}

function toPanelCase(match) {
  // toPanelCase always keys off match.toLowerCase(), so PANEL_WORD must only
  // ever have lowercase keys — a mixed-case key here (e.g. "WaferID") is
  // unreachable dead code and previously caused this to return undefined,
  // which crashed on out.charAt(0) below for any "WaferId" match.
  const out = PANEL_WORD[match.toLowerCase()];
  if (!out) {
    return match;
  }
  if (/^[A-Z]/.test(match)) {
    return toSentenceCase(out);
  }
  return out;
}

export function derivePanelKey(key) {
  if (typeof key !== 'string' || !WORD_RE.test(key)) {
    return null;
  }
  WORD_RE.lastIndex = 0;
  return key.replace(WORD_RE, toPanelCase);
}

function isPanelPlatformModeEnabled() {
  const state = store.getState();
  return Boolean(state?.common?.featureFlags?.isPanelPlatformModeEnabled);
}

function rawTerminologyReplace(value) {
  let final = value;

  Object.keys(PANEL_WORD).forEach(element => {
    final = final
      .replaceAll(element, PANEL_WORD[element])
      .replaceAll(toSentenceCase(element), toSentenceCase(PANEL_WORD[element]));
  });

  return final;
}

const panelTerminologyPostProcessor = {
  type: 'postProcessor',
  name: 'panelTerminology',
  process(value, key, options, translator) {
    if (!isPanelPlatformModeEnabled()) {
      return value;
    }

    const rawKey = Array.isArray(key) ? key[0] : key;
    const panelKey = derivePanelKey(rawKey);

    if (!panelKey || panelKey === rawKey) {
      return rawTerminologyReplace(value);
    }

    // `exists` (unlike `t`) never falls back to echoing the key, so it's
    // the only reliable way to tell "not translated" apart from a
    // legitimate identity translation (e.g. en resources are commonly
    // keyed by their own source string, "panel": "panel").
    if (!translator.exists(panelKey, { ...options, postProcess: false })) {
      return rawTerminologyReplace(value);
    }

    return translator.translate(panelKey, { ...options, postProcess: false });
  },
};

let lastFlagValue = isPanelPlatformModeEnabled();
store.subscribe(() => {
  const next = isPanelPlatformModeEnabled();
  if (next !== lastFlagValue) {
    lastFlagValue = next;
    i18n.emit('languageChanged', i18n.language);
  }
});

export default panelTerminologyPostProcessor;
